import React, { useCallback, useMemo, useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Alert, Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useGetTranslationOverviewLocales } from '~/hooks/apis/customer-communication/useTranslationOverview';
import useDebounced from '~/hooks/useDebounced';

import CoverageCell from './CoverageCell';
import LocaleCatalogsDrawer from './LocaleCatalogsDrawer';
import SelectionActionBar from './SelectionActionBar';
import TranslationOverviewToolbar from './TranslationOverviewToolbar';
import UploadStatusCell from './UploadStatusCell';
import {
  MUTATE_BLOCKED_REASON,
  SEARCH_DEBOUNCE_MS,
  TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE,
  TRANSLATION_OVERVIEW_PAGE_SIZES,
  formatPairCount,
} from './translationOverviewUtils';
import type { TranslationOverviewLocaleRow, TranslationOverviewStatus, TranslationOverviewUploadFilter } from './types';

const UPLOAD_BLOCKED_REASON = 'Every catalog must be Ready for a locale before it can be uploaded';

interface Props {
  onTranslate: (locales: Array<string>, pairCount: number) => void;
  onRefresh: (locales: Array<string>, pairCount: number) => void;
  onUpload: (locales: Array<string>) => void;
  canMutate: boolean;
}

function getColumns({
  onOpenMenu,
}: {
  onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: TranslationOverviewLocaleRow) => void;
}): Array<GridColDef<TranslationOverviewLocaleRow>> {
  return [
    {
      field: 'locale',
      headerName: 'Locale',
      width: 140,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" fontWeight={600}>
          {row.locale}
        </Typography>
      ),
    },
    {
      field: 'readyCount',
      headerName: 'Catalog coverage',
      flex: 1.6,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => <CoverageCell counts={row} total={row.totalCatalogs} />,
    },
    {
      field: 'failedCount',
      headerName: 'Failed',
      width: 110,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" color={row.failedCount ? 'error.main' : 'text.secondary'} fontWeight={600}>
          {row.failedCount.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'isUploadable',
      headerName: 'Upload',
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row.isUploadable ? (
          <Stack direction="row" spacing={0.5} alignItems="center" color="success.main">
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600}>
              Ready
            </Typography>
          </Stack>
        ) : (
          <Tooltip title={UPLOAD_BLOCKED_REASON} placement="top">
            <Typography variant="body2" color="text.secondary">
              Blocked
            </Typography>
          </Tooltip>
        ),
    },
    {
      field: 'pendingUploadCount',
      headerName: 'Braze',
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <UploadStatusCell counts={row} isUploadable={row.isUploadable} blockedReason={UPLOAD_BLOCKED_REASON} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      align: 'right',
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          aria-label={`Actions for ${row.locale}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenMenu(event, row);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
}

export default function LocalesTab({ onTranslate, onRefresh, onUpload, canMutate }: Props) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: TRANSLATION_OVERVIEW_DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'locale', sort: 'asc' }]);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<TranslationOverviewStatus | ''>('');
  const [uploadStatus, setUploadStatus] = useState<TranslationOverviewUploadFilter | ''>('');
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [menuRow, setMenuRow] = useState<TranslationOverviewLocaleRow | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [openLocale, setOpenLocale] = useState<TranslationOverviewLocaleRow | null>(null);

  const search = useDebounced(searchInput, SEARCH_DEBOUNCE_MS);

  const listParams = useMemo(
    () => ({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      search: search.trim() || undefined,
      status: status || undefined,
      uploadStatus: uploadStatus || undefined,
      sortBy: sortModel[0]?.field,
      sortDirection: sortModel[0]?.sort ?? undefined,
    }),
    [paginationModel, search, status, uploadStatus, sortModel],
  );

  const { data, isFetching, error } = useGetTranslationOverviewLocales(listParams);
  const rows = data?.rows ?? [];
  const rowCount = data?.total ?? 0;

  const columns = useMemo(
    () =>
      getColumns({
        onOpenMenu: (event, row) => {
          setMenuAnchor(event.currentTarget);
          setMenuRow(row);
        },
      }),
    [],
  );

  // Selection survives paging (`keepNonExistentRowsSelected`), so every action submits the whole
  // selection - the same set the action bar counts. Intersecting with `rows` would silently narrow
  // Upload to the current page while Translate and Refresh acted on all of it. Rows from other
  // pages are not loaded, so the current page only ever feeds the estimate in the summary copy,
  // never the scope of the request; a locale whose catalogs are not all Ready is rejected by the
  // API and reported per locale.
  const selectedLocales = useMemo(() => selectedIds.map(String), [selectedIds]);
  // A selected locale stands for every catalog in that locale, so the pair maths uses the widest row.
  const catalogsPerLocale = rows.length ? Math.max(...rows.map((row) => row.totalCatalogs)) : 0;
  const selectedPairCount = selectedIds.length * catalogsPerLocale;

  const resetToFirstPage = useCallback(() => {
    setPaginationModel((model) => ({ ...model, page: 0 }));
    setSelectedIds([]);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuRow(null);
  }, []);

  const handleMenuAction = useCallback(
    (action: 'translate' | 'refresh' | 'upload' | 'view') => {
      if (!menuRow) {
        return;
      }

      if (action === 'view') {
        setOpenLocale(menuRow);
      } else if (action === 'translate') {
        onTranslate([menuRow.locale], menuRow.totalCatalogs);
      } else if (action === 'refresh') {
        onRefresh([menuRow.locale], menuRow.totalCatalogs);
      } else {
        onUpload([menuRow.locale]);
      }

      handleCloseMenu();
    },
    [menuRow, onTranslate, onRefresh, onUpload, handleCloseMenu],
  );

  return (
    <Stack spacing={2}>
      <TranslationOverviewToolbar
        search={searchInput}
        onSearchChange={(value) => {
          setSearchInput(value);
          resetToFirstPage();
        }}
        searchPlaceholder="Search locales"
        status={status}
        onStatusChange={(value) => {
          setStatus(value);
          resetToFirstPage();
        }}
        uploadStatus={uploadStatus}
        onUploadStatusChange={(value) => {
          setUploadStatus(value);
          resetToFirstPage();
        }}
      />

      <SelectionActionBar
        selectedCount={selectedIds.length}
        itemNoun="locale"
        summary={`Acts on roughly ${formatPairCount(selectedPairCount)} across every catalog of the selected locales.`}
        actions={[
          {
            label: 'Translate missing',
            onClick: () => onTranslate(selectedLocales, catalogsPerLocale),
            disabled: !canMutate,
            disabledReason: MUTATE_BLOCKED_REASON,
          },
          {
            label: 'Refresh',
            onClick: () => onRefresh(selectedLocales, catalogsPerLocale),
            disabled: !canMutate,
            disabledReason: MUTATE_BLOCKED_REASON,
          },
          {
            label: 'Upload to Braze',
            onClick: () => onUpload(selectedLocales),
            disabled: !canMutate,
            disabledReason: MUTATE_BLOCKED_REASON,
          },
        ]}
        onClear={() => setSelectedIds([])}
      />

      {!!error && <Alert severity="error">{error.message}</Alert>}

      <Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.locale}
          loading={isFetching}
          rowCount={rowCount}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model);
            resetToFirstPage();
          }}
          pageSizeOptions={TRANSLATION_OVERVIEW_PAGE_SIZES}
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={setSelectedIds}
          disableRowSelectionOnClick
          onRowClick={({ row }) => setOpenLocale(row)}
          slots={{ pagination: GridPagination }}
          autoHeight
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleMenuAction('view')}>View catalogs</MenuItem>
        <MenuItem disabled={!canMutate} onClick={() => handleMenuAction('translate')}>
          Translate missing
        </MenuItem>
        <MenuItem disabled={!canMutate} onClick={() => handleMenuAction('refresh')}>
          Refresh translations
        </MenuItem>
        <MenuItem disabled={!canMutate || !menuRow?.isUploadable} onClick={() => handleMenuAction('upload')}>
          Upload to Braze
        </MenuItem>
      </Menu>

      <LocaleCatalogsDrawer
        locale={openLocale}
        onUpload={(locale) => onUpload([locale])}
        onClose={() => setOpenLocale(null)}
        canMutate={canMutate}
      />
    </Stack>
  );
}
